/**
 * Collection target types
 *
 * Types for managing collection targets, bulk status updates,
 * schedule import/export, and endpoint collection history.
 */

// =============================================================================
// ENUM TYPES
// =============================================================================

/**
 * Status of a collection target
 */
export type TargetStatus =
  | 'pending'
  | 'in_progress'
  | 'collecting'
  | 'collected'
  | 'partial'
  | 'failed'
  | 'client_halted'
  | 'cancelled'
  | 'ingested'

// =============================================================================
// ENTITY TYPES
// =============================================================================

/**
 * Collection target with endpoint details (API response)
 */
export interface CollectionTarget {
  id: string
  collectionRequestId: string
  endpointId: string
  evidenceId: string | null
  /**
   * Whether any evidence linked to this target has been chain-of-custody
   * received (`is_data_returned`). `false` (incl. no linked evidence) blocks
   * collection package upload.
   */
  evidenceReceived: boolean
  /**
   * Whether a collection receipt (uploaded package) already exists for this
   * target. This is the authoritative "uploaded" signal -- `targetStatus ===
   * 'returned'` means CoC-received, not uploaded.
   */
  hasReceipt: boolean
  endpointHostname: string
  endpointDeviceType: string
  endpointOs: string | null
  endpointLocation: string | null
  collectionMethod: string
  priority: string
  targetStatus: string
  assignedTo: string | null
  startedAt: string | null
  completedAt: string | null
  packageFilename: string | null
  packageHashSha256: string | null
  errorDescription: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Target dashboard summary
 */
export interface CollectionTargetSummary {
  totalTargets: number
  statusCounts: StatusCountEntry[]
  progressPercentage: number
  healthStatus: string
}

/**
 * Status count entry for dashboard
 */
export interface StatusCountEntry {
  status: string
  count: number
}

// =============================================================================
// INPUT TYPES
// =============================================================================

/**
 * Input for bulk status update
 */
export interface BulkStatusUpdateInput {
  targetIds: string[]
  newStatus: string
  note?: string
}

/**
 * Result of a bulk status update
 */
export interface BulkStatusUpdateResult {
  updatedCount: number
  failedCount: number
  failures: BulkStatusFailure[]
}

/**
 * Individual target failure in bulk update
 */
export interface BulkStatusFailure {
  targetId: string
  reason: string
}

/**
 * Input for re-collection
 */
export interface RecollectInput {
  targetIds: string[]
  artefactTemplateId?: string
  artefactOverrides?: Record<string, unknown>
  notes?: string
}

/**
 * Query parameters for listing targets
 */
export interface ListTargetsParams {
  status?: string
  hostname?: string
  priority?: string
}

// =============================================================================
// SCHEDULE TYPES
// =============================================================================

/**
 * Result of schedule CSV import
 */
export interface ScheduleImportResult {
  updatedCount: number
  unchangedCount: number
  unmatchedCount: number
  errorCount: number
}

// =============================================================================
// HISTORY TYPES
// =============================================================================

/**
 * Collection history for an endpoint
 */
export interface EndpointCollectionHistory {
  endpointId: string
  entries: CollectionHistoryEntry[]
}

/**
 * Single collection history entry
 */
export interface CollectionHistoryEntry {
  collectionRequestId: string
  requestName: string
  requestCode: string | null
  targetId: string
  evidenceId: string | null
  targetStatus: string
  createdAt: string
}

// =============================================================================
// ENHANCED REQUEST TYPES
// =============================================================================

/**
 * Input for enhanced collection request creation
 */
export interface CreateEnhancedCollectionRequestInput {
  requestName: string
  collectorType: string
  targetEndpointIds?: string[]
  artefactTemplateId?: string
  artefactOverrides?: Record<string, unknown>
  collectionOptions?: Record<string, unknown>
  notes?: string
}

/**
 * Response for enhanced collection request creation
 */
export interface EnhancedCreateResponse {
  request: import('./collection-requests').CollectionRequest
  evidenceIds: string[]
}

// =============================================================================
// DISPLAY CONSTANTS
// =============================================================================

/**
 * Labels for target status
 */
export const TARGET_STATUS_LABELS: Record<TargetStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  collecting: 'Collecting',
  collected: 'Collected',
  partial: 'Partial',
  failed: 'Failed',
  client_halted: 'Client Halted',
  cancelled: 'Cancelled',
  ingested: 'Ingested',
}

/**
 * Colours for target status badges
 */
export const TARGET_STATUS_COLOURS: Record<TargetStatus, string> = {
  pending: 'gray',
  in_progress: 'blue',
  collecting: 'blue',
  collected: 'green',
  partial: 'orange',
  failed: 'red',
  client_halted: 'orange',
  cancelled: 'gray',
  ingested: 'green',
}

/**
 * Health status labels for dashboard
 */
export const HEALTH_STATUS_LABELS: Record<string, string> = {
  on_track: 'On Track',
  stalled: 'Stalled',
  blocked: 'Blocked',
}

// =============================================================================
// MONITORING TYPES
// =============================================================================

/**
 * Simplified status for collection monitoring (tri-state)
 */
export type SimplifiedStatus = 'requested' | 'returned' | 'issue'

/**
 * Single item in the monitoring table
 */
export interface MonitoringItem {
  targetId: string
  endpointId: string
  hostname: string
  deviceType: string | null
  operatingSystem: string | null
  ipAddresses: string[]
  evidenceId: string | null
  targetStatus: string
  simplifiedStatus: SimplifiedStatus
  collectionNumber: number
  collectionRequestId: string
  requestName: string
  requestCode: string
  requestStatus: string
  completedAt: string | null
  errorDescription: string | null
  notes: string | null
}

/**
 * Aggregate summary statistics for monitoring
 */
export interface MonitoringSummary {
  totalTargets: number
  requestedCount: number
  returnedCount: number
  issueCount: number
  recollectionCount: number
  uniqueEndpoints: number
}

/**
 * Top-level monitoring API response
 */
export interface CollectionMonitoringResponse {
  items: MonitoringItem[]
  summary: MonitoringSummary
}

/**
 * Filter parameters for the monitoring endpoint
 */
export interface MonitoringFilterParams {
  status?: SimplifiedStatus
  requestId?: string
  hostname?: string
}

/**
 * Labels for simplified status
 */
export const SIMPLIFIED_STATUS_LABELS: Record<SimplifiedStatus, string> = {
  requested: 'Requested',
  returned: 'Returned',
  issue: 'Issue',
}

/**
 * Colours for simplified status badges (CSS custom property names)
 */
export const SIMPLIFIED_STATUS_COLOURS: Record<SimplifiedStatus, string> = {
  requested: 'var(--accent)',
  returned: 'var(--success)',
  issue: 'var(--danger)',
}
